import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatBRL = (value: number | null | undefined) => {
  if (value == null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
};

export const formatUSD = (value: number | null | undefined) => {
  if (value == null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
};

export const formatNumber = (value: number | null | undefined, digits = 1) => {
  if (value == null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
};

const MONTHS_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Formata data como dd/Mmm/yyyy (ex.: 29/Jul/2024).
 *
 * ⚠️ NÃO trocar por conversão de fuso (`Intl` com `timeZone`, ou os getters
 * locais `getDate`/`getMonth`/`getFullYear`).
 *
 * Todos os campos que passam por aqui — `interaction_date`, `deadline`,
 * `call_date`, `distribution_date`, `start_date`, `investment_period_end` —
 * são colunas `date` do Postgres: data pura, sem hora. Chegam ao frontend como
 * meia-noite UTC. Convertê-las para UTC-3 desloca a exibição um dia para trás,
 * e a tela passa a mostrar 28/Jul para uma chamada cadastrada em 29/Jul.
 *
 * Isso foi testado em produção em 03/ago/2026 e revertido: para data pura, o
 * correto é ler em UTC, que devolve exatamente o dia armazenado.
 *
 * Conversão de fuso só faz sentido para `timestamptz` de verdade (`created_at`,
 * `updated_at`), que marcam um instante — esses não usam esta função.
 */
export const formatDate = (value: string | Date | null | undefined) => {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getUTCDate()).padStart(2, "0")}/${MONTHS_EN[d.getUTCMonth()]}/${d.getUTCFullYear()}`;
};

export const formatPercent = (value: number | null | undefined, digits = 2) => {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${formatNumber(value, digits)}%`;
};

/**
 * Limpa artefatos de importação CSV nos campos array (person_of_contact, email).
 * Lida com strings como '["André Soares\nBernardo Costa"]' ou '["a@b.com","c@d.com"]'
 * que vieram do CSV com newlines literais ou vírgulas dentro de colchetes/aspas.
 */
export function cleanArrayField(arr: string[]): string[] {
  return arr
    .flatMap((item) => {
      const t = item.trim();
      // Tenta JSON.parse primeiro (lida com ["a","b"] e ["a\nb"])
      try {
        const parsed = JSON.parse(t);
        if (Array.isArray(parsed)) {
          return (parsed as unknown[])
            .flatMap((v) => String(v).split(/\n/).map((s) => s.trim()))
            .filter(Boolean);
        }
        return [String(parsed).trim()].filter(Boolean);
      } catch {
        // Fallback: remoção manual de colchetes e aspas
      }
      const inner = t.startsWith("[") && t.endsWith("]") ? t.slice(1, -1) : t;
      return inner
        .split(/,|\n/)
        .map((s) => s.trim().replace(/^["\\ ]+|["\\ ]+$/g, "").trim())
        .filter(Boolean);
    })
    .filter(Boolean);
}
